/**
 * Client-side password gate for custom booths.
 *
 * The manifest carries a `passwordHash` + `passwordSalt`. When set, the
 * booth and gallery pages prompt for the password and only render once
 * the user-entered value hashes to the stored hash. Success is cached in
 * sessionStorage so the user only enters it once per session per booth.
 *
 * This is intentionally a UI lock — direct S3 / share URLs to individual
 * captures bypass it. The builder copy makes that caveat explicit.
 */
import type { BoothManifest } from '@photobooth/shared';

const SS_PREFIX = 'booth-pw:';

export async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const arr = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < arr.length; i++) {
    hex += arr[i]!.toString(16).padStart(2, '0');
  }
  return hex;
}

export async function checkPassword(
  manifest: BoothManifest,
  password: string,
): Promise<boolean> {
  if (!manifest.passwordHash || !manifest.passwordSalt) return true;
  const hex = await sha256Hex(`${manifest.passwordSalt}:${password}`);
  // Simple comparison — this is a soft gate.
  return hex === manifest.passwordHash;
}

export function manifestRequiresPassword(manifest: BoothManifest): boolean {
  return Boolean(manifest.passwordHash && manifest.passwordSalt);
}

export function isUnlocked(slug: string): boolean {
  try {
    return sessionStorage.getItem(SS_PREFIX + slug) === '1';
  } catch {
    return false;
  }
}

export function rememberUnlocked(slug: string): void {
  try {
    sessionStorage.setItem(SS_PREFIX + slug, '1');
  } catch {
    /* sessionStorage disabled */
  }
}
